import React, { useState, useContext, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocumentIcon,
  Receipt as ReceiptIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import Dashboard from '../../Layouts/Dashboard';
import HeaderActions from '../../Components/HeaderActions';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Define breadcrumbs for this page
const uploadDocumentsBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { href: '/accounting/dashboard', label: 'Comptabilité' },
  { label: 'Ajouter Documents' }
];

const UploadDocumentsContent = () => {
  const { sendRequestWithToast } = useContext(HttpRequestContext);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [processingResults, setProcessingResults] = useState(null);

  // Mock transaction data
  const mockTransactions = [
    {
      id: 1,
      date: '2024-01-15',
      description: 'Facture électricité EDF',
      amount: -156.78,
      account: 'Compte Principal',
      status: 'needs_document',
      category: 'Utilities'
    },
    {
      id: 2,
      date: '2024-01-14',
      description: 'Achat matériel bureau',
      amount: -234.56,
      account: 'Carte de Crédit',
      status: 'needs_document',
      category: 'Office Supplies'
    },
    {
      id: 3,
      date: '2024-01-13',
      description: 'Restaurant - déjeuner client',
      amount: -45.80,
      account: 'Compte Principal',
      status: 'needs_document',
      category: 'Meals & Entertainment'
    },
    {
      id: 4,
      date: '2024-01-12',
      description: 'Carburant station service',
      amount: -67.90,
      account: 'Carte de Crédit',
      status: 'needs_document',
      category: 'Transportation'
    },
    {
      id: 5,
      date: '2024-01-11',
      description: 'Abonnement logiciel comptabilité',
      amount: -89.99,
      account: 'Compte Principal',
      status: 'needs_document',
      category: 'Software'
    }
  ];

  const documentTypes = [
    { value: 'invoice', label: 'Facture', icon: <ReceiptIcon /> },
    { value: 'receipt', label: 'Reçu', icon: <ReceiptIcon /> },
    { value: 'contract', label: 'Contrat', icon: <DocumentIcon /> },
    { value: 'bank_statement', label: 'Relevé bancaire', icon: <DocumentIcon /> },
    { value: 'expense_report', label: 'Note de frais', icon: <DocumentIcon /> },
    { value: 'other', label: 'Autre', icon: <AttachFileIcon /> }
  ];

  // File upload handling
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploaded',
      preview: URL.createObjectURL(file),
      linkedTransaction: selectedTransaction,
      documentType: documentType,
      description: documentDescription
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, [selectedTransaction, documentType, documentDescription]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    multiple: true
  });

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) {
      return <PdfIcon color="error" />;
    }
    if (type.includes('image')) {
      return <ImageIcon color="primary" />;
    }
    return <DocumentIcon color="action" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'dd MMM yyyy', { locale: fr });
  };

  const filteredTransactions = mockTransactions.filter(transaction =>
    transaction.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    transaction.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const linkToTransaction = (file, transaction) => {
    setUploadedFiles(prev => prev.map(f => 
      f.id === file.id 
        ? { ...f, linkedTransaction: transaction, status: 'linked' }
        : f
    ));
  };

  const previewDocument = (file) => {
    setPreviewFile(file);
    setShowPreviewDialog(true);
  };

  const processDocuments = async () => {
    setIsProcessing(true);
    
    // Simulate processing
    setTimeout(() => {
      const mockResults = {
        totalFiles: uploadedFiles.length,
        successfulFiles: uploadedFiles.length,
        failedFiles: 0,
        linkedDocuments: uploadedFiles.filter(f => f.linkedTransaction).length,
        unlinkedDocuments: uploadedFiles.filter(f => !f.linkedTransaction).length,
        categorizedDocuments: uploadedFiles.filter(f => f.documentType).length
      };
      
      setProcessingResults(mockResults);
      setIsProcessing(false);
    }, 2000);
  };

  return (
    <Box sx={{ p: 3 }}>
      <HeaderActions
        customActions={[
          <Button
            key="back-to-dashboard"
            variant="outlined"
            onClick={() => window.history.back()}
          >
            Retour au Tableau de Bord
          </Button>
        ]}
      />

      <Grid container spacing={3}>
        {/* Upload Area */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Télécharger des Documents
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Ajoutez des justificatifs pour vos transactions : factures, reçus, contrats, etc.
            </Typography>

            {/* Document Configuration */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type de document</InputLabel>
                  <Select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    label="Type de document"
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {type.icon}
                          <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description (optionnel)"
                  value={documentDescription}
                  onChange={(e) => setDocumentDescription(e.target.value)}
                  placeholder="Ex: Facture électricité janvier 2024"
                />
              </Grid>
            </Grid>

            {/* Upload Zone */}
            <Paper
              {...getRootProps()}
              sx={{
                p: 4,
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                backgroundColor: isDragActive ? 'primary.light' : 'grey.50',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                mb: 3
              }}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Déposez vos documents ici' : 'Glissez-déposez vos documents ou cliquez pour sélectionner'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Formats acceptés: PDF, Images (PNG, JPG), Documents Word, TXT
              </Typography>
            </Paper>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Documents téléchargés ({uploadedFiles.length})
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Document</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Transaction liée</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadedFiles.map((file) => (
                        <TableRow key={file.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getFileIcon(file.type)}
                              <Box sx={{ ml: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  {file.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {file.documentType ? (
                              <Chip 
                                label={documentTypes.find(t => t.value === file.documentType)?.label || 'Non défini'} 
                                size="small" 
                              />
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Non défini
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {file.linkedTransaction ? (
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {file.linkedTransaction.description}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {formatCurrency(file.linkedTransaction.amount)} - {formatDate(file.linkedTransaction.date)}
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                size="small"
                                startIcon={<LinkIcon />}
                                onClick={() => setShowTransactionDialog(true)}
                              >
                                Lier à une transaction
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={file.status === 'linked' ? 'Lié' : 'Non lié'}
                              color={file.status === 'linked' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Prévisualiser">
                              <IconButton size="small" onClick={() => previewDocument(file)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton size="small" color="error" onClick={() => removeFile(file.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={processDocuments}
                    disabled={isProcessing || uploadedFiles.length === 0}
                    size="large"
                  >
                    {isProcessing ? 'Traitement en cours...' : 'Enregistrer les Documents'}
                  </Button>
                </Box>

                {isProcessing && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* Processing Results */}
          {processingResults && (
            <Paper sx={{ p: 3 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Documents enregistrés avec succès</AlertTitle>
                {processingResults.successfulFiles} documents ont été traités.
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Résumé du traitement
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Documents traités:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {processingResults.successfulFiles}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Documents liés:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {processingResults.linkedDocuments}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Documents non liés:</Typography>
                        <Typography variant="body2" fontWeight="bold" color="warning.main">
                          {processingResults.unlinkedDocuments}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => window.location.href = '/accounting/dashboard'}
                  sx={{ mr: 1 }}
                >
                  Retour au Tableau de Bord
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                >
                  Ajouter d'autres Documents
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Transactions Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Transactions sans justificatifs
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Sélectionnez une transaction pour y associer vos documents.
            </Typography>

            <TextField
              fullWidth
              placeholder="Rechercher une transaction..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
              sx={{ mb: 2 }}
            />

            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredTransactions.map((transaction) => (
                <ListItem
                  key={transaction.id}
                  button
                  onClick={() => setSelectedTransaction(transaction)}
                  selected={selectedTransaction?.id === transaction.id}
                  sx={{ 
                    border: '1px solid',
                    borderColor: selectedTransaction?.id === transaction.id ? 'primary.main' : 'grey.300',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                      <WarningIcon fontSize="small" />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {transaction.description}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="textSecondary">
                          {formatDate(transaction.date)} • {transaction.account}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="error.main">
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {selectedTransaction && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Transaction sélectionnée: <strong>{selectedTransaction.description}</strong>
                </Typography>
                <Typography variant="caption">
                  Les prochains documents téléchargés seront automatiquement liés à cette transaction.
                </Typography>
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Transaction Selection Dialog */}
      <Dialog
        open={showTransactionDialog}
        onClose={() => setShowTransactionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Lier à une transaction
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Rechercher une transaction..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
            sx={{ mb: 2 }}
          />
          
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {filteredTransactions.map((transaction) => (
              <ListItem
                key={transaction.id}
                button
                onClick={() => {
                  setSelectedTransaction(transaction);
                  setShowTransactionDialog(false);
                }}
              >
                <ListItemText
                  primary={transaction.description}
                  secondary={`${formatCurrency(transaction.amount)} - ${formatDate(transaction.date)} - ${transaction.account}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTransactionDialog(false)}>
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Aperçu: {previewFile?.name}
        </DialogTitle>
        <DialogContent>
          {previewFile && (
            <Box sx={{ textAlign: 'center' }}>
              {previewFile.type.includes('image') ? (
                <img
                  src={previewFile.preview}
                  alt={previewFile.name}
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                />
              ) : (
                <Box sx={{ p: 4 }}>
                  {getFileIcon(previewFile.type)}
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {previewFile.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Aperçu non disponible pour ce type de fichier
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main component wrapped with Dashboard layout
const UploadDocuments = () => {
  return (
    <Dashboard 
      dashboardTitle="Ajouter Documents Justificatifs"
      breadcrumbs={uploadDocumentsBreadcrumbs}
    >
      <UploadDocumentsContent />
    </Dashboard>
  );
};

export default UploadDocuments; 